import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Tooltip,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Fade,
  Divider,
  Paper,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import api from '../services/api';
import aiService, { isAiLimitError } from '../services/aiService';
import itemService from '../services/itemService';
import letGoService from '../services/letGoService';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import { mergeTagList } from '../utils/tagMerge';
import OfflineImage from './OfflineImageSimple';
import AIProcessingIndicator from './AIProcessingIndicator';
import VoiceInputButton from './VoiceInputButton';
import { useAppNavigation } from '../hooks/useAppNavigation';

// Import styling from our design system
import { colors, shadows } from '../styles/styleConstants';
import { modalStyles } from '../styles/componentStyles';

const ItemDetailModal = ({ open, onClose, itemId, fromPage, onItemsAdded }) => {
  const navigate = useAppNavigation();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // State for fullscreen image dialog
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  // State for fullscreen description dialog
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  // Store the source page to handle back navigation
  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manualTagInput, setManualTagInput] = useState('');
  const [manualTags, setManualTags] = useState([]);
  const [selectedBox, setSelectedBox] = useState('');
  const [boxes, setBoxes] = useState([]);
  const [boxName, setBoxName] = useState('');
  // Operation states
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isStructuringVoice, setIsStructuringVoice] = useState(false);
  const [aiLimitReached, setAiLimitReached] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // State for Exit Bin
  const [isAddingToExitBin, setIsAddingToExitBin] = useState(false);
  // State for related items (shared image)
  const [relatedItems, setRelatedItems] = useState(null);
  // State for viewing the original shared group image
  const [groupImageDialogOpen, setGroupImageDialogOpen] = useState(false);

  // Define fetch functions before useEffect that calls them
  const fetchItemData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Online-only mode
      const isActuallyOffline = !navigator.onLine;
      
      // Log online status
      console.log(`ItemDetailModal: Online status: ${!isActuallyOffline}`);
      
      if (isActuallyOffline) {
        throw new Error('You are currently offline. Please check your internet connection and try again.');
      }
      
      // Fetch item data from API with cache-busting query parameter
      const timestamp = new Date().getTime();
      const response = await api.get(`/items/${itemId}?_cb=${timestamp}`);
      const itemData = response.data;
      console.log('ItemDetailModal: Item data from API:', itemData);
      console.log('ItemDetailModal: Tags in item data:', {
        tags: itemData.tags,
        custom_tags: itemData.custom_tags,
        tagsType: typeof itemData.tags,
        tagsIsArray: Array.isArray(itemData.tags),
        tagsLength: itemData.tags ? itemData.tags.length : 'undefined'
      });
      setItem(itemData);
    } catch (err) {
      console.error('Error fetching item details:', err);
      setError('Failed to load item details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [itemId]);

  const fetchBoxes = useCallback(async () => {
    try {
      // Fetch boxes from API
      const response = await api.get('/boxes');
      const boxesData = response.data;
      console.log('ItemDetailModal: Boxes data from API:', boxesData);
      
      // Set boxes
      setBoxes(boxesData || []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      // No need to set an error message here as this is a secondary fetch
    }
  }, []); // Remove item dependency to prevent infinite loop

  const fetchRelatedItems = useCallback(async () => {
    try {
      if (!itemId) return;
      const response = await api.get(`/items/${itemId}/related`);
      const relatedData = response.data;
      console.log('ItemDetailModal: Related items data:', relatedData);
      
      // Construct proxy URL from shared_image_id
      if (relatedData.shared_image_id && relatedData.user_id) {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        relatedData.shared_image_url = `${baseUrl}/api/v2/files/shared/${relatedData.shared_image_id}/${relatedData.user_id}`;
        console.log('ItemDetailModal: Constructed shared_image_url:', relatedData.shared_image_url);
      }
      
      setRelatedItems(relatedData);
    } catch (error) {
      console.error('Error fetching related items:', error);
      setRelatedItems(null);
    }
  }, [itemId]);

  useEffect(() => {
    // Only fetch if the modal is open and we have an itemId
    if (open && itemId) {
      fetchItemData();
      fetchBoxes();
      fetchRelatedItems();
    }
  }, [open, itemId, fetchItemData, fetchBoxes, fetchRelatedItems]);

  // Update box name when item or boxes change
  useEffect(() => {
    if (item && item.box_id && boxes.length > 0) {
      const matchingBox = boxes.find(box => box.id === item.box_id);
      if (matchingBox) {
        setBoxName(matchingBox.name);
      }
    } else if (item && item.box && item.box.name) {
      setBoxName(item.box.name);
    }
  }, [item, boxes]);

  // Reset edit mode when modal is closed
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setSuccess(false);
      setError(null);
      setAiLimitReached(false);
    }
  }, [open]);

  // Update form fields when item changes
  useEffect(() => {
    console.log('ItemDetailModal: useEffect triggered with item:', item);
    if (item) {
      console.log('ItemDetailModal: Setting form fields from item data');
      setName(item.name || '');
      setDescription(item.description || '');
      setSelectedBox(item.box_id || '');
      
      // Set tags from item with debugging
      console.log('ItemDetailModal: Processing item for tags:', {
        itemId: item.id,
        tags: item.tags,
        custom_tags: item.custom_tags,
        tagsType: typeof item.tags,
        tagsIsArray: Array.isArray(item.tags),
        customTagsType: typeof item.custom_tags
      });
      
      if (item.tags && Array.isArray(item.tags)) {
        console.log('ItemDetailModal: Setting tags from item.tags:', item.tags);
        setManualTags(item.tags);
      } else if (item.custom_tags && Array.isArray(item.custom_tags)) {
        console.log('ItemDetailModal: Setting tags from item.custom_tags (array):', item.custom_tags);
        setManualTags(item.custom_tags);
      } else if (item.custom_tags && typeof item.custom_tags === 'string' && item.custom_tags.trim()) {
        // Handle comma-separated string format
        const tagsFromString = item.custom_tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        console.log('ItemDetailModal: Setting tags from item.custom_tags (string):', tagsFromString);
        setManualTags(tagsFromString);
      } else {
        console.log('ItemDetailModal: No valid tags found, setting empty array');
        setManualTags([]);
      }
    }
  }, [item]);

  // Poll for AI processing status updates when item is being processed
  useEffect(() => {
    if (!item || !open) return;
    
    const isProcessing = item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing';
    
    if (isProcessing) {
      const pollInterval = setInterval(async () => {
        try {
          const timestamp = new Date().getTime();
          const response = await api.get(`/items/${itemId}?_cb=${timestamp}`);
          const updatedItem = response.data;
          
          // Check if processing status changed
          if (updatedItem.ai_processing_status !== item.ai_processing_status) {
            console.log('ItemDetailModal: AI processing status changed:', updatedItem.ai_processing_status);
            setItem(updatedItem);
            
            // Update form fields if processing completed
            if (updatedItem.ai_processing_status === 'completed') {
              setDescription(updatedItem.description || '');
              if (updatedItem.tags && Array.isArray(updatedItem.tags)) {
                setManualTags(updatedItem.tags);
              } else if (updatedItem.custom_tags) {
                const tags = typeof updatedItem.custom_tags === 'string' 
                  ? updatedItem.custom_tags.split(',').map(t => t.trim()).filter(t => t)
                  : updatedItem.custom_tags;
                setManualTags(tags);
              }
            }
          }
        } catch (err) {
          console.error('Error polling AI processing status:', err);
        }
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [item, itemId, open]);

  // Navigation is now handled by the parent component (FindItems)
  // Just call onClose which will trigger navigation in the parent

  const handleBoxClick = () => {
    if (item && item.box_id) {
      // Close the modal first
      onClose();
      // Navigate to the box detail page
      navigate.to.BOX_DETAIL(item.box_id);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (item) {
      setName(item.name || '');
      setDescription(item.description || '');
      setSelectedBox(item.box_id || '');
    }
    
    setIsEditMode(false);
    setError(null);
    setSuccess(false);
    setAiLimitReached(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Online-only mode check
      if (!navigator.onLine) {
        throw new Error('You are currently offline. Please check your internet connection and try again.');
      }
      
      // Prepare updated item data
      const updatedItem = {
        ...item,
        name,
        description,
        box_id: selectedBox,
        tags: manualTags
      };

      // Save the item with cache busting
      const timestamp = new Date().getTime();
      await api.put(`/items/${itemId}?_cb=${timestamp}`, updatedItem);
      
      // Refresh item data
      const response = await api.get(`/items/${itemId}?_cb=${timestamp}`);
      setItem(response.data);
      
      // Show success message and exit edit mode
      setSuccess(true);
      setIsEditMode(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteItem = async () => {
    try {
      console.log('ItemDetailModal: Starting item deletion for item', itemId);
      setIsDeleting(true);
      // Use itemService to ensure cache is invalidated
      await itemService.deleteItem(itemId);
      console.log('ItemDetailModal: Item deleted successfully, closing modal');
      onClose(); // Close the modal after deletion
    } catch (err) {
      console.error('ItemDetailModal: Error deleting item:', err);
      setError('Failed to delete item. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleVoiceTranscript = async (transcript) => {
    try {
      setIsStructuringVoice(true);
      const result = await aiService.structureTranscript(transcript, name || null);
      setDescription((result && result.description) || transcript);
      if (result && Array.isArray(result.tags) && result.tags.length) {
        setManualTags((prev) => mergeTagList(prev, result.tags));
      }
    } catch (err) {
      console.error('Voice structuring failed, using raw transcript:', err);
      setDescription(transcript);
    } finally {
      setIsStructuringVoice(false);
    }
  };

  const handleUpgradeClick = () => {
    onClose();
    navigate.to.SUBSCRIPTION();
  };

  const handleAddManualTags = () => {
    // Split input by commas, trim, and filter empty ones
    const newTags = manualTagInput.split(',').map(t => t.trim()).filter(t => t);
    if (newTags.length === 0) return;
    
    // Add to manual tags, avoiding duplicates
    setManualTags(prev => {
      const combined = [...prev, ...newTags];
      // Remove duplicates - case insensitive
      return Array.from(new Set(combined.map(t => t.toLowerCase())))
        .map(t => {
          // Find original case version
          const original = combined.find(o => o.toLowerCase() === t);
          return original;
        });
    });
    setManualTagInput('');
  };

  const generateDescriptionAndTags = async () => {
    if (!item || !item.id) return;
    
    try {
      setIsGeneratingDescription(true);
      setIsExtracting(true);
      setError(null);
      setAiLimitReached(false);
      setSuccess(false);
      
      console.log('Queuing AI processing for description and tags...');
      
      // Queue the processing - returns immediately
      const queueResponse = await aiService.extractDescriptionAndTags(item.id);
      console.log('Queue response:', queueResponse);
      
      // If processing is queued, poll for completion
      if (queueResponse.status === 'processing') {
        console.log('AI processing queued, polling for completion...');
        
        // Poll with status updates
        const result = await aiService.pollItemStatus(item.id, (status, updatedItem) => {
          console.log(`AI processing status: ${status}`);
        });
        
        // Handle completed result
        if (result.status === 'completed') {
          if (result.description) {
            setDescription(result.description);
          }
          if (result.tags && Array.isArray(result.tags)) {
            setManualTags(prev => {
              const combined = [...prev, ...result.tags];
              return Array.from(new Set(combined.map(t => t.toLowerCase())))
                .map(t => combined.find(o => o.toLowerCase() === t));
            });
          }
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
          
          // Signal box refresh for multi-item detection
          if (result.item && result.item.box_id) {
            const timestamp = Date.now();
            localStorage.setItem('cubxly_items_added', timestamp.toString());
            localStorage.setItem('cubxly_box_updated', result.item.box_id.toString());
            localStorage.setItem('cubxly_force_refresh', timestamp.toString());
          }
        } else if (result.status === 'content_rejected') {
          setError('Image content was rejected. Please use a different image.');
        }
      } else if (queueResponse.description) {
        // Legacy: synchronous response (shouldn't happen with new backend)
        setDescription(queueResponse.description);
        if (queueResponse.tags && Array.isArray(queueResponse.tags)) {
          setManualTags(prev => {
            const combined = [...prev, ...queueResponse.tags];
            return Array.from(new Set(combined.map(t => t.toLowerCase())))
              .map(t => combined.find(o => o.toLowerCase() === t));
          });
        }
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error generating description and extracting tags:', err);
      if (isAiLimitError(err)) {
        setAiLimitReached(true);
        setError(null);
      } else {
        setError('Failed to generate description and extract tags. Please try again.');
      }
    } finally {
      setIsGeneratingDescription(false);
      setIsExtracting(false);
    }
  };

  // Legacy method for description only (kept for backward compatibility)
  const generateDescription = async () => {
    if (!item || !item.id) return;
    
    try {
      setIsGeneratingDescription(true);
      setError(null);
      setAiLimitReached(false);
      setSuccess(false); // Reset success state
      
      const response = await aiService.generateDescription(item.id);
      console.log('AI Description response:', response);
      
      // Handle both response formats (direct or nested in data property)
      if (response && response.description) {
        setDescription(response.description);
        setSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else if (response && response.data && response.data.description) {
        setDescription(response.data.description);
        setSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error('No description found in API response');
      }
    } catch (err) {
      console.error('Error generating AI description:', err);
      if (isAiLimitError(err)) {
        setAiLimitReached(true);
        setError(null);
      } else {
        setError('Failed to generate AI description. Please try again.');
      }
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Legacy method for tags only (kept for backward compatibility)
  const extractTags = async () => {
    if (!item || !item.id) return;
    
    try {
      setIsExtracting(true);
      setError(null);
      setAiLimitReached(false);

      const response = await aiService.extractTags(item.id);
      
      if (response.data && response.data.tags && Array.isArray(response.data.tags)) {
        setManualTags(prev => {
          const combined = [...prev, ...response.data.tags];
          // Remove duplicates - case insensitive
          return Array.from(new Set(combined.map(t => t.toLowerCase())))
            .map(t => {
              // Find original case version
              const original = combined.find(o => o.toLowerCase() === t);
              return original;
            });
        });
      }
    } catch (err) {
      console.error('Error extracting tags:', err);
      if (isAiLimitError(err)) {
        setAiLimitReached(true);
        setError(null);
      } else {
        setError('Failed to extract tags. Please try again.');
      }
    } finally {
      setIsExtracting(false);
    }
  };

  // Add item to Exit Bin
  const handleAddToExitBin = async () => {
    if (!item || !item.id) return;
    
    try {
      setIsAddingToExitBin(true);
      setError(null);
      
      console.log('Adding item to exit bin:', item.id);
      const result = await letGoService.recordDecision(item.id, 'donate');
      console.log('Exit bin result:', result);
      
      if (result && result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onClose(); // Close modal after adding to exit bin
        }, 1500);
      } else {
        setError(result?.error || 'Failed to add item to Exit Bin.');
      }
    } catch (err) {
      console.error('Error adding to exit bin:', err);
      setError('Failed to add item to Exit Bin. Please try again.');
    } finally {
      setIsAddingToExitBin(false);
    }
  };

  const renderTagChips = () => {
    if (!item && !isEditMode) return null;
    
    // Use manualTags for display
    const allTags = [...manualTags];
    
    // Sort tags alphabetically
    allTags.sort();
    
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        {allTags.map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            size="small"
            icon={<LocalOfferIcon />}
            onDelete={isEditMode ? () => {
              setManualTags(prev => prev.filter((_, i) => i !== index));
            } : undefined}
            sx={{ mb: 1 }}
          />
        ))}
        {allTags.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tags added to this item
          </Typography>
        )}
      </Stack>
    );
  };

  const renderViewMode = () => (
    <Grid container spacing={4}>
      {/* Image Section */}
      <Grid item xs={12} sm={6}>
        <Paper 
          elevation={0}
          sx={{ 
            width: '100%', 
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'rgba(0,0,0,0.02)',
            p: 2,
            border: '1px solid rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: shadows.md,
              transform: 'scale(1.01)'
            }
          }}
          onClick={() => setImageDialogOpen(true)}
        >
          <OfflineImage
            src={getBestItemImageUrl(item)}
            itemId={item.id}
            alt={item.name || 'Item image'}
            fallbackSrc={placeholderImage}
            sx={{ 
              width: '100%', 
              maxHeight: '300px',
              objectFit: 'contain',
              borderRadius: 2
            }}
          />
        </Paper>
      </Grid>
      
      {/* Details Section */}
      <Grid item xs={12} sm={6}>
        <Box sx={{ mt: { xs: 2, sm: 0 } }}>
          <Typography variant="h5" gutterBottom sx={{ 
            color: colors.darkSlateBlue,
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            mb: 2
          }}>
            {item.name}
          </Typography>
          
          <Paper
            elevation={0}
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 2, 
              backgroundColor: 'rgba(94, 108, 132, 0.05)',
              border: '1px solid rgba(94, 108, 132, 0.1)'
            }}
          >
            <Typography variant="body1" sx={{ color: colors.mediumSlateBlue, fontWeight: 600, mb: 0.5 }}>
              Location
            </Typography>
            {item.box_id ? (
              <Link
                component="button"
                variant="body2"
                onClick={handleBoxClick}
                sx={{ 
                  color: colors.actionBlue,
                  textDecoration: 'none',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: colors.darkSlateBlue,
                  }
                }}
              >
                {item.box?.name || boxName || `Box #${item.box_id}`}
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            ) : (
              <Typography variant="body2" sx={{ color: colors.darkSlateBlue }}>
                No box assigned
              </Typography>
            )}
          </Paper>
          {/* Related Items Section - shows when this photo also contains items in other boxes */}
          {relatedItems && relatedItems.has_shared_image && (
            <Paper
              elevation={0}
              sx={{ 
                p: 2, 
                mt: 2,
                borderRadius: 2, 
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                border: '1px solid rgba(255, 152, 0, 0.3)'
              }}
            >
              <Typography variant="body1" sx={{ color: '#e65100', fontWeight: 600, mb: 1 }}>
                📷 Photo shows {relatedItems.total_items || 1} items
              </Typography>
              {relatedItems.items_in_other_boxes && relatedItems.items_in_other_boxes.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ color: colors.charcoal, mb: 1 }}>
                    This photo also contains items in other boxes:
                  </Typography>
                  <Stack spacing={0.5}>
                    {relatedItems.items_in_other_boxes.map((relItem) => (
                      <Box key={relItem.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: colors.darkSlateBlue }}>
                          • <strong>{relItem.name}</strong> → {relItem.box_name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
              {relatedItems.items_in_same_box && relatedItems.items_in_same_box.length > 0 && (
                <Typography variant="body2" sx={{ color: colors.mediumSlateBlue, mt: 1, fontStyle: 'italic' }}>
                  + {relatedItems.items_in_same_box.length} other item(s) in this box
                </Typography>
              )}
              {relatedItems.shared_image_url && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => setGroupImageDialogOpen(true)}
                    sx={{ borderRadius: 2 }}
                  >
                    View group photo
                  </Button>
                </Box>
              )}
            </Paper>
          )}
          
        </Box>
      </Grid>
      
      {/* AI Processing Indicator - Shows when item is being processed */}
      {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (
        <Grid item xs={12}>
          <AIProcessingIndicator 
            status={item.ai_processing_status}
            isGroupPhoto={relatedItems?.has_shared_image || false}
            groupPhotoItemCount={relatedItems?.total_items || 0}
            variant="banner"
          />
        </Grid>
      )}
      
      {/* Description Section - Full Width */}
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ 
          color: colors.darkSlateBlue, 
          fontWeight: 600,
          mb: 1
        }}>
          Description
        </Typography>
        <Box sx={{ 
          mb: 3, 
          color: colors.charcoal,
          lineHeight: 1.8,
          backgroundColor: 'rgba(0,0,0,0.02)',
          p: 2,
          borderRadius: 2,
          fontSize: '0.875rem',
          whiteSpace: 'pre-wrap',
          position: 'relative'
        }}>
          {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && !item.description ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.mediumSlateBlue }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                AI is generating description...
              </Typography>
            </Box>
          ) : item.description ? item.description.split('\n').map((line, i) => (
            <Box key={i} sx={{ mb: line.startsWith('**') ? 1 : 0.5 }}>
              {line.replace(/\*\*(.*?)\*\*/g, '').trim() ? (
                <>
                  {line.includes('**') ? (
                    <span>
                      <strong>{line.match(/\*\*(.*?)\*\*/)?.[1] || ''}</strong>
                      {line.replace(/\*\*(.*?)\*\*/, '')}
                    </span>
                  ) : line}
                </>
              ) : null}
            </Box>
          )) : 'No description provided.'}
        </Box>
      </Grid>
      
      {/* Tags Section - Full Width */}
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ 
          color: colors.darkSlateBlue, 
          fontWeight: 600,
          mb: 1
        }}>
          Tags
        </Typography>
        {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (!item.tags || item.tags.length === 0) && (!manualTags || manualTags.length === 0) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.mediumSlateBlue, p: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              AI is extracting tags...
            </Typography>
          </Box>
        ) : renderTagChips()}
      </Grid>
    </Grid>
  );

  const renderEditMode = () => (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={3}>
        {/* Image Section */}
        <Grid item xs={12} sm={6}>
          <Box 
            sx={{ 
              width: '100%', 
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setImageDialogOpen(true)}
          >
            <OfflineImage
              src={getBestItemImageUrl(item)}
              itemId={item.id}
              alt={item.name || 'Item image'}
              fallbackSrc={placeholderImage}
              sx={{ 
                width: '100%', 
                maxHeight: '300px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Grid>
        
        {/* Edit Form Section - Name and Box */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Item Name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="box-select-label">Box</InputLabel>
            <Select
              labelId="box-select-label"
              id="box-select"
              value={selectedBox}
              label="Box"
              onChange={(e) => setSelectedBox(e.target.value)}
            >
              {boxes.map((box) => (
                <MenuItem key={box.id} value={box.id}>
                  {box.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Description Section - Full Width Below Image */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              id="description"
              label="Description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isStructuringVoice}
              helperText={isStructuringVoice ? 'Structuring your dictation…' : undefined}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Tooltip title={isGeneratingDescription || isExtracting ? 'AI is currently generating...' : 'Generate Description & Tags (Recommended)'}>
                <span>
                  <IconButton
                    onClick={generateDescriptionAndTags}
                    disabled={isGeneratingDescription || isExtracting}
                    color="primary"
                  >
                    {(isGeneratingDescription || isExtracting) ? <CircularProgress size={24} /> : <AutoFixHighIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <VoiceInputButton
                onInterim={(text) => setDescription(text)}
                onTranscript={handleVoiceTranscript}
                disabled={isStructuringVoice || isGeneratingDescription}
                tooltip="Dictate description & tags"
              />
              <Tooltip title={isGeneratingDescription ? 'AI is currently generating...' : 'Generate Description Only'}>
                <span>
                  <IconButton 
                    onClick={generateDescription} 
                    disabled={isGeneratingDescription}
                    color="secondary"
                    size="small"
                  >
                    {isGeneratingDescription ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Expand Description">
                <IconButton 
                  onClick={() => setDescriptionDialogOpen(true)}
                  color="default"
                  size="small"
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
        
        {/* Tags Section - Full Width */}
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
            Tags:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              id="tags"
              label="Add Tags (comma separated)"
              name="tags"
              value={manualTagInput}
              onChange={(e) => setManualTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualTagInput.trim()) {
                  e.preventDefault();
                  handleAddManualTags();
                }
              }}
              onBlur={() => {
                if (manualTagInput.trim()) {
                  handleAddManualTags();
                }
              }}
            />
            <Tooltip title={isExtracting ? 'AI is currently extracting tags...' : 'Extract Tags Only (Legacy)'}>
              <span>
                <IconButton 
                  onClick={extractTags} 
                  disabled={isExtracting}
                  size="small"
                  color="secondary"
                  sx={{ ml: 1, mt: 1 }}
                >
                  {isExtracting ? <CircularProgress size={24} /> : <LocalOfferIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {renderTagChips()}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      {/* Dialog for viewing the original shared group image */}
      <Dialog
        open={groupImageDialogOpen}
        onClose={() => setGroupImageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Group Photo</Typography>
          <IconButton onClick={() => setGroupImageDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {relatedItems && relatedItems.shared_image_url ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <OfflineImage
                src={relatedItems.shared_image_url}
                alt="Group photo containing this item and others"
                fallbackSrc={placeholderImage}
                sx={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Typography variant="body2">Group photo not available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            ...modalStyles.paper,
            maxWidth: { xs: '100%', sm: '600px', md: '900px' },
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={{ 
          ...modalStyles.title,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          mb: 1
        }}>
          <Typography 
            variant="h5" 
            component="div"
            sx={{ 
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              color: colors.darkSlateBlue
            }}
          >
            {isLoading ? 'Loading Item...' : (
              isEditMode ? 'Edit Item' : (item ? item.name : 'Item Details')
            )}
          </Typography>
          <Box>
            {item && !isEditMode && (
              <Tooltip title="Edit Item" arrow placement="top">
                <IconButton 
                  onClick={handleEditClick} 
                  edge="end" 
                  aria-label="edit"
                  sx={{ 
                    mr: 1,
                    color: colors.actionBlue
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton 
              onClick={onClose} 
              edge="end" 
              aria-label="back"
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: colors.mediumSlateBlue,
                '&:hover': {
                  backgroundColor: 'rgba(94, 108, 132, 0.08)',
                  color: colors.darkSlateBlue,
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, ...modalStyles.content }}>
          {aiLimitReached && (
            <Alert
              severity="info"
              sx={{ borderRadius: 2, mb: 2 }}
              onClose={() => setAiLimitReached(false)}
              action={
                <Button color="inherit" size="small" onClick={handleUpgradeClick}>
                  Upgrade
                </Button>
              }
            >
              AI assist covers your first 100 items on the Free plan. Use the mic to dictate
              descriptions and tags — it's free forever — or upgrade for unlimited AI.
            </Alert>
          )}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, height: '300px' }}>
              <CircularProgress sx={{ color: colors.actionBlue }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiAlert-icon': {
                color: colors.danger
              } 
            }}>
              {error}
            </Alert>
          ) : success ? (
            <Fade in={success}>
              <Alert severity="success" sx={{ 
                borderRadius: 2,
                mb: 3,
                '& .MuiAlert-icon': {
                  color: colors.success
                } 
              }}>
                Item updated successfully
              </Alert>
            </Fade>
          ) : item ? (
            <Box sx={{ px: { xs: 0, sm: 1 } }}>
              {isEditMode ? renderEditMode() : renderViewMode()}
            </Box>
          ) : (
            <Alert severity="warning" sx={{ 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: colors.warning
              } 
            }}>
              Item not found
            </Alert>
          )}
        </DialogContent>
        
        <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />
        
        <DialogActions sx={{ 
          p: 3, 
          justifyContent: isEditMode ? 'space-between' : 'flex-end',
          ...modalStyles.actions 
        }}>
          {isEditMode ? (
            <>
              <Button 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1
                }}
              >
                Delete
              </Button>
              <Box>
                <Button 
                  onClick={handleCancelEdit} 
                  startIcon={<CancelIcon />}
                  sx={{ 
                    mr: 2,
                    borderRadius: 2,
                    px: 2
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  onClick={handleSubmit}
                  variant="contained" 
                  color="primary"
                  startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={isSaving}
                  sx={{
                    backgroundColor: colors.actionBlue,
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: colors.actionBlue,
                      transform: 'translateY(-2px)',
                      boxShadow: shadows.md,
                    }
                  }}
                >
                  Save
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
              <Tooltip title={item?.exit_bin_status === 'maybe_donate' ? 'Already in Exit Bin' : 'Add to Exit Bin for donation consideration'}>
                <span>
                  <Button 
                    onClick={handleAddToExitBin}
                    variant="outlined"
                    color="warning"
                    startIcon={isAddingToExitBin ? <CircularProgress size={20} /> : <VolunteerActivismIcon />}
                    disabled={isAddingToExitBin || item?.exit_bin_status === 'maybe_donate'}
                    sx={{
                      borderRadius: 2,
                      px: 2
                    }}
                  >
                    {item?.exit_bin_status === 'maybe_donate' ? 'In Exit Bin' : 'Exit Bin'}
                  </Button>
                </span>
              </Tooltip>
              <Button 
                onClick={onClose}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  px: 3
                }}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Fullscreen Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            bgcolor: colors.darkSlateBlue,
            height: '100%',
            maxHeight: '100vh',
            m: 0,
            borderRadius: 0,
            boxShadow: 'none'
          }
        }}
      >
        <IconButton
          onClick={() => setImageDialogOpen(false)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease-in-out',
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            overflow: 'auto',
            p: 2
          }}
          onClick={() => setImageDialogOpen(false)}
        >
          {item && (
            <Box
              component="img"
              src={getBestItemImageUrl(item)}
              alt={item.name || 'Item image'}
              crossOrigin="anonymous"
              onError={(e) => { e.target.src = placeholderImage; }}
              sx={{ 
                // Scale small images up to a minimum size while respecting max bounds
                minWidth: { xs: '80vw', sm: '60vw', md: '50vw' },
                minHeight: { xs: '40vh', sm: '50vh', md: '60vh' },
                maxWidth: '95vw',
                maxHeight: 'calc(100vh - 64px)',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0px 10px 30px rgba(0,0,0,0.3)'
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* Fullscreen Description Dialog */}
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: "'Poppins', sans-serif", 
          fontWeight: 600, 
          borderBottom: `1px solid ${colors.divider}`,
          pb: 2,
          color: colors.darkSlateBlue,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Edit Description
          <IconButton onClick={() => setDescriptionDialogOpen(false)}>
            <FullscreenExitIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <TextField
            fullWidth
            multiline
            minRows={15}
            maxRows={30}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter item description..."
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.divider}` }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Generate Description & Tags">
              <span>
                <IconButton 
                  onClick={generateDescriptionAndTags} 
                  disabled={isGeneratingDescription || isExtracting}
                  color="primary"
                >
                  {(isGeneratingDescription || isExtracting) ? <CircularProgress size={24} /> : <AutoFixHighIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Button 
            onClick={() => setDescriptionDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: colors.actionBlue,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                backgroundColor: colors.actionBlue,
              }
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
            maxWidth: '500px'
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
      >
        <DialogTitle sx={{ 
          fontFamily: "'Poppins', sans-serif", 
          fontWeight: 600, 
          borderBottom: `1px solid ${colors.divider}`,
          pb: 2,
          color: colors.darkSlateBlue
        }}>
          Delete Item
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <DialogContentText sx={{ color: colors.mediumSlateBlue }}>
            Are you sure you want to delete this item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pt: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
            sx={{ borderRadius: 2, px: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={deleteItem} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
            sx={{ borderRadius: 2, px: 2 }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemDetailModal;
